import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { electronicInvoicingApi } from "../../api/electronicInvoicingApi";

type InvoiceElectronicStatus =
  | "DRAFT"
  | "XML_READY"
  | "SENT_TO_ARUBA"
  | "SENT_TO_SDI"
  | "DELIVERED"
  | "NOT_DELIVERED"
  | "REJECTED"
  | "ERROR";

type InvoiceLine = {
  id: string;
  description: string;
  quantity: number | string;
  unitPrice: number | string;
  vatRate?: number | string | null;
  total: number | string;
};

type InvoiceNotification = {
  id: string;
  notificationType: string;
  status: string;
  message?: string | null;
  receivedAt: string;
  payload?: any;
};

type InvoiceDetail = {
  id: string;
  number: string;
  year: number;
  customerName: string;
  customerVat?: string | null;
  customerFiscalCode?: string | null;
  issueDate: string;
  total: number | string;
  currency?: string;
  electronicStatus?: InvoiceElectronicStatus;
  status?: InvoiceElectronicStatus;
  provider?: string | null;
  externalId?: string | null;
  sdiIdentifier?: string | null;
  xmlContent?: string | null;
  createdAt?: string;
  updatedAt?: string;
  lines?: InvoiceLine[];
  notifications?: InvoiceNotification[];
};

type GenericApiResponse = {
  ok?: boolean;
  message?: string;
  data?: any;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "short" }).format(date);
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", { dateStyle: "short", timeStyle: "short" }).format(date);
}

function formatAmount(value: unknown, currency = "EUR") {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(num);
}

function getStatusLabel(status: InvoiceElectronicStatus) {
  switch (status) {
    case "DRAFT":
      return "Bozza";
    case "XML_READY":
      return "XML pronto";
    case "SENT_TO_ARUBA":
      return "Inviata ad Aruba";
    case "SENT_TO_SDI":
      return "Inviata a SDI";
    case "DELIVERED":
      return "Consegnata";
    case "NOT_DELIVERED":
      return "Non recapitata";
    case "REJECTED":
      return "Scartata";
    case "ERROR":
      return "Errore";
    default:
      return status;
  }
}

function getStatusBadgeClass(status: InvoiceElectronicStatus) {
  switch (status) {
    case "DRAFT":
      return "text-bg-secondary";
    case "XML_READY":
      return "text-bg-info";
    case "SENT_TO_ARUBA":
    case "SENT_TO_SDI":
      return "text-bg-primary";
    case "DELIVERED":
      return "text-bg-success";
    case "NOT_DELIVERED":
      return "text-bg-warning";
    case "REJECTED":
    case "ERROR":
      return "text-bg-danger";
    default:
      return "text-bg-light";
  }
}

function normalizeStatus(invoice?: InvoiceDetail | null): InvoiceElectronicStatus {
  return (invoice?.electronicStatus ?? invoice?.status ?? "DRAFT") as InvoiceElectronicStatus;
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth() as any;

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "warning" | "info";
    text: string;
  } | null>(null);

  const loadInvoice = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setAlert(null);
      const data = (await electronicInvoicingApi.getInvoice(token, id)) as InvoiceDetail | null;
      if (!data) {
        setInvoice(null);
        return;
      }
      const notifications = await electronicInvoicingApi.listInvoiceNotifications(token, id).catch(() => []);
      setInvoice({ ...data, notifications });
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore nel caricamento dettaglio fattura",
      });
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  async function handleGenerateXml() {
    if (!id) return;
    try {
      setBusy(true);
      setAlert(null);
      const result = (await electronicInvoicingApi.generateXml(token, id)) as GenericApiResponse;
      setAlert({
        type: "success",
        text: result?.message || "XML generato correttamente.",
      });
      await loadInvoice();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore durante la generazione XML",
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleSendToAruba(isResend = false) {
    if (!id) return;
    try {
      setBusy(true);
      setAlert(null);
      const result = (await electronicInvoicingApi.sendInvoice(token, id)) as GenericApiResponse;
      setAlert({
        type: "success",
        text: result?.message || (isResend ? "Fattura reinviata ad Aruba." : "Fattura inviata ad Aruba."),
      });
      await loadInvoice();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error?.message || "Errore durante invio ad Aruba",
      });
    } finally {
      setBusy(false);
    }
  }

  const totals = useMemo(() => {
    const lines = invoice?.lines || [];
    const taxable = lines.reduce((sum, line) => {
      const total = Number(line.total || 0);
      return sum + (Number.isNaN(total) ? 0 : total);
    }, 0);
    return {
      taxable,
      grandTotal: Number(invoice?.total || 0),
    };
  }, [invoice]);

  const status = normalizeStatus(invoice);
  const canGenerateXml = status === "DRAFT" || status === "ERROR";
  const canSend = status === "XML_READY" || status === "NOT_DELIVERED";
  const canResend = status === "REJECTED" || status === "NOT_DELIVERED" || status === "ERROR";

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
        <div>
          <div className="mb-2">
            <Link to="/invoices" className="text-decoration-none">
              ← Torna alle fatture
            </Link>
          </div>
          <h1 className="h3 mb-1">Dettaglio fattura {invoice ? `${invoice.number}/${invoice.year}` : ""}</h1>
          <p className="text-muted mb-0">Visualizza dati, XML e notifiche della fattura elettronica.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-outline-secondary" onClick={loadInvoice} disabled={loading || busy}>
            {loading ? "Aggiornamento..." : "Aggiorna"}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleGenerateXml}
            disabled={!canGenerateXml || busy || loading}
          >
            {busy && canGenerateXml ? "Generazione..." : "Genera XML"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSendToAruba(false)}
            disabled={!canSend || busy || loading}
          >
            {busy && canSend ? "Invio..." : "Invia ad Aruba"}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => handleSendToAruba(true)}
            disabled={!canResend || busy || loading}
          >
            {busy && canResend ? "Reinvio..." : "Reinvia"}
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} role="alert">
          {alert.text}
        </div>
      )}

      {loading && !invoice ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">Caricamento dettaglio fattura...</div>
        </div>
      ) : !invoice ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center text-muted">Fattura non trovata.</div>
        </div>
      ) : (
        <div className="row g-4">
          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Informazioni fattura</h2>
                <div className="mb-2">
                  <strong>Numero:</strong> {invoice.number}/{invoice.year}
                </div>
                <div className="mb-2">
                  <strong>Data emissione:</strong> {formatDate(invoice.issueDate)}
                </div>
                <div className="mb-2">
                  <strong>Totale:</strong> {formatAmount(invoice.total, invoice.currency || "EUR")}
                </div>
                <div className="mb-2">
                  <strong>Provider:</strong> {invoice.provider || "—"}
                </div>
                <div className="mb-2">
                  <strong>ID esterno:</strong> {invoice.externalId || "—"}
                </div>
                <div className="mb-2">
                  <strong>ID SDI:</strong> {invoice.sdiIdentifier || "—"}
                </div>
                <div className="mb-0">
                  <strong>Stato:</strong> <span className={`badge ${getStatusBadgeClass(status)}`}>{getStatusLabel(status)}</span>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Cliente</h2>
                <div className="mb-2">
                  <strong>Nome:</strong> {invoice.customerName}
                </div>
                <div className="mb-2">
                  <strong>P.IVA:</strong> {invoice.customerVat || "—"}
                </div>
                <div className="mb-0">
                  <strong>Codice fiscale:</strong> {invoice.customerFiscalCode || "—"}
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h2 className="h5 mb-3">Riepilogo</h2>
                <div className="d-flex justify-content-between mb-2">
                  <span>Imponibile</span>
                  <strong>{formatAmount(totals.taxable, invoice.currency || "EUR")}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Totale documento</span>
                  <strong>{formatAmount(totals.grandTotal, invoice.currency || "EUR")}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <h2 className="h5 mb-3">Righe fattura</h2>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Descrizione</th>
                        <th>Q.ta</th>
                        <th>Prezzo</th>
                        <th>IVA</th>
                        <th>Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!invoice.lines || invoice.lines.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            Nessuna riga documento disponibile.
                          </td>
                        </tr>
                      ) : (
                        invoice.lines.map((line) => (
                          <tr key={line.id}>
                            <td>{line.description}</td>
                            <td>{line.quantity}</td>
                            <td>{formatAmount(line.unitPrice, invoice.currency || "EUR")}</td>
                            <td>{line.vatRate !== null && line.vatRate !== undefined ? `${line.vatRate}%` : "—"}</td>
                            <td>{formatAmount(line.total, invoice.currency || "EUR")}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h2 className="h5 mb-0">Anteprima XML</h2>
                  <span className="small text-muted">Tracciato generato da NAT</span>
                </div>
                <div className="bg-light border rounded p-3" style={{ maxHeight: 420, overflow: "auto" }}>
                  <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                    {invoice.xmlContent || "XML non ancora generato."}
                  </pre>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h2 className="h5 mb-3">Storico notifiche Aruba / SDI</h2>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Stato</th>
                        <th>Messaggio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!invoice.notifications || invoice.notifications.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            Nessuna notifica disponibile.
                          </td>
                        </tr>
                      ) : (
                        invoice.notifications.map((n) => (
                          <tr key={n.id}>
                            <td>{formatDateTime(n.receivedAt)}</td>
                            <td>{n.notificationType}</td>
                            <td>
                              <span className="badge text-bg-light border">{n.status}</span>
                            </td>
                            <td>{n.message || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {!!invoice.notifications?.length && (
                  <div className="mt-3">
                    <details>
                      <summary>Mostra payload ultima notifica</summary>
                      <pre className="bg-light border rounded p-3 mt-2 small mb-0">
                        {JSON.stringify(invoice.notifications[invoice.notifications.length - 1]?.payload || {}, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
